import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { PageHeader } from '@/components/layout';
import { Button, Card } from '@/components/common';
import { getErrorMessage } from '@/services';
import { FLAG_TITLE, ledgerMatchService, type MatchFlag, type MatchParams } from '@/services/ledgerMatch';
import { formatCurrency, formatDate } from '@/utils';
import { currentFinancialYear } from './pospLedgerStatement';
import { ReconciliationTabs } from './ReconciliationTabs';

/**
 * #696 -- Reconciliation > Ledger match. The insurer ledger (left) and the POSP ledger (right) for one
 * financial year (1 Apr - 31 Mar) and optionally one month, one row per policy. Totals per insurer and per POSP;
 * picking an insurer narrows the POSPs to those who earned on its policies, and the reverse. A mismatch card's
 * count is a link that opens exactly the rows it counts (?flag=). Read-only.
 */
const SELECT = 'rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100';
const PAGE = 100;
const money = (v: string | null | undefined) => (v == null ? '-' : formatCurrency(Number(v)));
const th = 'px-3 py-2 text-xs font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-300';

export function LedgerMatchPage() {
  const [params, setParams] = useSearchParams();
  const fy = params.get('financial_year') || currentFinancialYear();
  const month = params.get('month') || '';
  const insurer = params.get('insurer') || '';
  const posp = params.get('posp') || '';
  const flag = (params.get('flag') as MatchFlag | null) ?? '';
  const [offset, setOffset] = useState(0);

  const set = (patch: Record<string, string>) => {
    const next = new URLSearchParams(params);
    for (const [k, v] of Object.entries(patch)) (v ? next.set(k, v) : next.delete(k));
    setOffset(0);
    setParams(next);
  };
  const query: MatchParams = { financial_year: fy, month, insurer, posp, flag, offset, limit: PAGE };
  const match = useQuery({ queryKey: ['ledgerMatch', query], queryFn: () => ledgerMatchService.match(query) });
  const data = match.data;
  const keep = (extra: string) => `financial_year=${fy}${month ? `&month=${month}` : ''}${insurer ? `&insurer=${insurer}` : ''}${posp ? `&posp=${posp}` : ''}${extra}`;
  const years = Array.from(new Set([...(data?.financial_years ?? []), fy])).sort().reverse();

  const exportCsv = () => ledgerMatchService.exportMatch(query).catch((e) => toast.error(getErrorMessage(e)));

  return (
    <div className="space-y-4">
      <PageHeader title="Ledger match" subtitle="Insurer and POSP ledgers side by side, policy by policy" />
      <ReconciliationTabs />
      <Card padding="md">
        <div className="flex flex-wrap items-end gap-3">
          <label className="text-sm text-gray-700 dark:text-gray-300">
            <span className="mb-1 block">Financial year</span>
            <select aria-label="Financial year" className={SELECT} value={fy} onChange={(e) => set({ financial_year: e.target.value, month: '' })}>
              {years.map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
          </label>
          <label className="text-sm text-gray-700 dark:text-gray-300">
            <span className="mb-1 block">Month (optional)</span>
            <input aria-label="Month" type="month" className={SELECT} value={month} min={data?.date_from.slice(0, 7)} max={data?.date_to.slice(0, 7)}
              onChange={(e) => set({ month: e.target.value })} />
          </label>
          {(insurer || posp || flag) && (
            <Button variant="outline" size="sm" onClick={() => set({ insurer: '', posp: '', flag: '' })}>Clear filters</Button>
          )}
          <div className="ml-auto">
            <Button variant="outline" size="sm" disabled={!data} onClick={exportCsv}>Export CSV</Button>
          </div>
        </div>
        <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
          Financial year {data ? `${data.date_from} to ${data.date_to}` : fy}. A policy sits in the year of its policy date. The insurer pays per month,
          so cash and TDS received are spread over that month&apos;s policies by commission. Confirmed receipts and completed payouts only.
        </p>
      </Card>

      {match.isError && <p role="alert" className="text-sm text-amber-700 dark:text-amber-400">Could not load the match. That is not the same as everything matching - please try again.</p>}

      {data && (
        <>
          <div className="grid gap-3 md:grid-cols-4">
            {data.cards.map((c) => (
              <Card key={c.flag} padding="md">
                <p className="text-xs text-gray-500 dark:text-gray-400">{FLAG_TITLE[c.flag]}</p>
                <Link
                  to={`/reconciliation/ledger-match?${keep(`&flag=${c.flag}`)}`}
                  aria-label={`${FLAG_TITLE[c.flag]}: ${c.count}`}
                  className="text-2xl font-semibold text-primary-700 hover:underline dark:text-primary-400"
                >
                  {c.count}
                </Link>
              </Card>
            ))}
          </div>

          <div className="grid gap-4 lg:grid-cols-2">
            <Card padding="md">
              <h3 className="mb-2 text-sm font-semibold">Insurer ledger (per insurer)</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm" aria-label="Per insurer">
                  <thead><tr><th className={`${th} text-left`}>Insurer</th><th className={`${th} text-right`}>Commission</th><th className={`${th} text-right`}>Received</th><th className={`${th} text-right`}>Outstanding</th><th className={`${th} text-right`}>Mismatches</th></tr></thead>
                  <tbody>
                    {data.by_insurer.map((i) => (
                      <tr key={i.insurer ?? 'none'} className={`border-t border-gray-100 dark:border-gray-800 ${i.insurer === insurer ? 'bg-primary-50 dark:bg-gray-800' : ''}`}>
                        <td className="px-3 py-2">
                          {i.insurer ? (
                            <button type="button" className="underline" aria-pressed={i.insurer === insurer} onClick={() => set({ insurer: i.insurer === insurer ? '' : i.insurer!, posp: '' })}>{i.insurer_name}</button>
                          ) : i.insurer_name}
                        </td>
                        <td className="px-3 py-2 text-right">{money(i.insurer_commission)}</td>
                        <td className="px-3 py-2 text-right">{money(String(Number(i.insurer_received) + Number(i.insurer_tds)))}</td>
                        <td className="px-3 py-2 text-right">{money(i.insurer_outstanding)}</td>
                        <td className="px-3 py-2 text-right">{i.mismatches}</td>
                      </tr>
                    ))}
                    {data.by_insurer.length === 0 && <tr><td colSpan={5} className="px-3 py-3 text-gray-500">No insurer commission in this view.</td></tr>}
                  </tbody>
                </table>
              </div>
            </Card>
            <Card padding="md">
              <h3 className="mb-2 text-sm font-semibold">POSP ledger (per POSP)</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm" aria-label="Per POSP">
                  <thead><tr><th className={`${th} text-left`}>POSP</th><th className={`${th} text-right`}>Share payable</th><th className={`${th} text-right`}>Paid</th><th className={`${th} text-right`}>Outstanding</th></tr></thead>
                  <tbody>
                    {data.by_posp.map((p) => (
                      <tr key={p.posp ?? 'none'} className={`border-t border-gray-100 dark:border-gray-800 ${p.posp === posp ? 'bg-primary-50 dark:bg-gray-800' : ''}`}>
                        <td className="px-3 py-2">
                          {p.posp === null ? <span>{p.posp_code}</span> : <button type="button" className="underline" aria-pressed={p.posp === posp} onClick={() => set({ posp: p.posp === posp ? '' : p.posp!, insurer: '' })}>{p.posp_code} {p.posp_name}</button>}
                        </td>
                        <td className="px-3 py-2 text-right">{money(p.posp_share)}</td>
                        <td className="px-3 py-2 text-right">{money(p.posp_paid)}</td>
                        <td className="px-3 py-2 text-right">{money(p.posp_outstanding)}</td>
                      </tr>
                    ))}
                    {data.by_posp.length === 0 && <tr><td colSpan={4} className="px-3 py-3 text-gray-500">No POSP lines in this view.</td></tr>}
                  </tbody>
                </table>
              </div>
            </Card>
          </div>

          <Card padding="none">
            <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3 dark:border-gray-800">
              <h3 className="text-sm font-semibold">
                {flag ? FLAG_TITLE[flag] : 'All policies'} ({data.count})
              </h3>
              {flag && <Button size="sm" variant="secondary" onClick={() => set({ flag: '' })}>Show all</Button>}
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm" aria-label="Ledger match">
                <thead className="bg-gray-50 dark:bg-gray-800">
                  <tr>
                    <th className={`${th} text-left`}>Date</th><th className={`${th} text-left`}>Policy</th><th className={`${th} text-left`}>Insurer</th>
                    <th className={`${th} text-left`}>POSP</th>
                    <th className={`${th} text-right`}>Insurer commission</th><th className={`${th} text-right`}>POSP share</th><th className={`${th} text-right`}>Broker margin</th>
                    <th className={`${th} text-right`}>Insurer received</th><th className={`${th} text-right`}>Insurer outstanding</th>
                    <th className={`${th} text-right`}>POSP paid</th><th className={`${th} text-right`}>POSP outstanding</th><th className={`${th} text-left`}>Mismatch</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                  {data.results.map((r) => (
                    <tr key={r.id} data-flags={r.flags.join(' ')} className="text-gray-800 dark:text-gray-200">
                      <td className="px-3 py-2 whitespace-nowrap">{formatDate(r.date)}</td>
                      <td className="px-3 py-2 whitespace-nowrap">{r.policy_id ? <Link className="underline" to={`/policies/${r.policy_id}`}>{r.policy_number}</Link> : r.policy_number}</td>
                      <td className="px-3 py-2">{r.insurer_name ?? ''}</td>
                      <td className="px-3 py-2">{[...r.posps.map((p) => p.code), ...(r.no_posp ? ['(no POSP)'] : [])].join(', ')}</td>
                      <td className="px-3 py-2 text-right">{money(r.insurer_commission)}</td>
                      <td className="px-3 py-2 text-right">{money(r.posp_share)}</td>
                      <td className="px-3 py-2 text-right">{money(r.broker_margin)}</td>
                      <td className="px-3 py-2 text-right">{money(String(Number(r.insurer_received) + Number(r.insurer_tds)))}</td>
                      <td className="px-3 py-2 text-right">{money(r.insurer_outstanding)}</td>
                      <td className="px-3 py-2 text-right">{money(r.posp_paid)}</td>
                      <td className="px-3 py-2 text-right">{money(r.posp_outstanding)}</td>
                      <td className="px-3 py-2 text-amber-700 dark:text-amber-400">{r.flags.map((f) => FLAG_TITLE[f]).join('; ')}</td>
                    </tr>
                  ))}
                  {data.results.length === 0 && <tr><td colSpan={12} className="px-3 py-4 text-gray-500">Nothing here.</td></tr>}
                  <tr data-testid="match-totals" className="font-semibold text-gray-900 dark:text-gray-100">
                    <td className="px-3 py-2" colSpan={4}>Total (all rows in view)</td>
                    <td className="px-3 py-2 text-right">{money(data.totals.insurer_commission)}</td>
                    <td className="px-3 py-2 text-right">{money(data.totals.posp_share)}</td>
                    <td className="px-3 py-2 text-right">{money(data.totals.broker_margin)}</td>
                    <td className="px-3 py-2 text-right">{money(String(Number(data.totals.insurer_received) + Number(data.totals.insurer_tds)))}</td>
                    <td className="px-3 py-2 text-right">{money(data.totals.insurer_outstanding)}</td>
                    <td className="px-3 py-2 text-right">{money(data.totals.posp_paid)}</td>
                    <td className="px-3 py-2 text-right">{money(data.totals.posp_outstanding)}</td>
                    <td />
                  </tr>
                </tbody>
              </table>
            </div>
            {data.count > PAGE && (
              <div className="flex items-center justify-between px-4 py-3 text-sm">
                <span>{offset + 1}-{Math.min(offset + PAGE, data.count)} of {data.count}</span>
                <span className="flex gap-2">
                  <Button size="sm" variant="secondary" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE))}>Previous</Button>
                  <Button size="sm" variant="secondary" disabled={offset + PAGE >= data.count} onClick={() => setOffset(offset + PAGE)}>Next</Button>
                </span>
              </div>
            )}
          </Card>
        </>
      )}
    </div>
  );
}

export default LedgerMatchPage;
